/**
 * Off-screen render target for the "Export war plan" feature.
 *
 * Composes, side by side:
 *   [ Bounded SVG map (active hexagons only, region labels, combat + hub markers) ]
 *   [ Static war-plan panel (current state, read-only) ]
 *
 * Designed to be rasterised by `html-to-image`. No interactions, no animations.
 */

import type { CSSProperties } from 'react';
import type {
  GriefingRisk,
  MapArrow,
  MapObject,
  OpFrequency,
  StockManagement,
  WarPlanDTO,
  WarStyle,
} from '@warplan/shared';
import {
  ATLAS,
  HEX_VERTICES,
  REGION_LABELS,
  TILE_W,
  TILE_H,
  findRegionLabel,
  tileAssetFor,
  type MapBackgroundKind,
} from '../lib/foxhole-maps.js';
import { getIconEntry } from '../lib/icon-catalog.js';
import { ExportArrows } from './ExportArrows.js';
import {
  resolveIconCollisions,
} from '../lib/resolve-icon-collisions.js';
import {
  EXPORT_HUB_IDS,
  EXPORT_MAP_MAX_HEIGHT_PX,
  EXPORT_MAP_WIDTH_PX,
  EXPORT_PANEL_WIDTH_PX,
  type ExportBoundingBox,
} from '../lib/war-plan-export.js';

const COMBAT_ORDER: Record<string, number> = {
  COMBAT_OBJECTIVE: 0,
  COMBAT_ATTACK: 1,
  COMBAT_SUPPORT: 2,
  COMBAT_PARTISAN: 3,
  COMBAT_PATROL: 4,
};

const HUB_ORDER: Record<string, number> = {
  LOGISTIC_HUB: 0,
  LOGISTIC_SEAPORT: 1,
  LOGISTIC_STORAGEDEPOT: 1,
  LOGISTIC_AIRWAY: 2,
};

/** Attribution shown on exported images when the 'ink' background mod is active. */
const INK_CREDIT = 'Fond de carte « Ink & Frontline » par Arkhitecton';

export interface ExportWarLabel {
  wx: number;
  wy: number;
  text: string;
  kind: 'major' | 'minor';
}

export interface WarPlanExportViewProps {
  bbox: ExportBoundingBox;
  /** Markers already filtered to keep only combat + hubs in active regions. */
  markers: ReadonlyArray<MapObject>;
  /**
   * Optional broader set of map objects used only to compute de-overlap
   * positions, ensuring the export arrangement matches the interactive view
   * (which sees all objects, not just the export subset). Markers absent from
   * this list still de-overlap among themselves.
   */
  collisionContext?: ReadonlyArray<MapObject>;
  /** Pre-filtered arrows to render on top of the map. */
  arrows: ReadonlyArray<MapArrow>;
  disabledRegionKeys: ReadonlySet<string>;
  mapBackground: MapBackgroundKind;
  warPlan: WarPlanDTO | null;
  /** WarAPI region labels (used to enrich marker rows with the nearest major). */
  warLabels: ReadonlyArray<ExportWarLabel>;
  /** Optional footer caption (e.g. generation timestamp). */
  caption?: string;
}

// ---------------------------------------------------------------------------
// Style helpers (must stay self-contained — html-to-image needs computed styles)
// ---------------------------------------------------------------------------

type Severity = 'red' | 'orange' | 'green';

const SEVERITY_BG: Record<Severity, string> = {
  red: '#b91c1c',
  orange: '#b45309',
  green: '#047857',
};
const SEVERITY_DOT: Record<Severity, string> = {
  red: '#fca5a5',
  orange: '#fcd34d',
  green: '#6ee7b7',
};

const WAR_STYLE_LABELS: Record<WarStyle, { fr: string; en: string; severity: Severity }> = {
  offensive: { fr: 'Offensive', en: 'Offensive', severity: 'red' },
  balanced: { fr: 'Équilibré', en: 'Balanced', severity: 'orange' },
  defensive: { fr: 'Défensive', en: 'Defensive', severity: 'green' },
};

const OP_FREQ_LABELS: Record<OpFrequency, { fr: string; en: string; severity: Severity }> = {
  high: { fr: 'Fréquence Haute', en: 'High Frequency', severity: 'red' },
  medium: { fr: 'Fréquence Moyenne', en: 'Medium Frequency', severity: 'orange' },
  low: { fr: 'Fréquence Basse', en: 'Low Frequency', severity: 'green' },
};

const STOCK_LABELS: Record<StockManagement, { fr: string; en: string; severity: Severity }> = {
  frontline_supply: { fr: 'Alimentation du front', en: 'Frontline supplying', severity: 'red' },
  balanced_use: { fr: 'Utilisation équilibrée', en: 'Balanced use', severity: 'green' },
};

const GRIEF_LABELS: Record<GriefingRisk, { fr: string; en: string; severity: Severity }> = {
  high: { fr: 'Risque élevé', en: 'High risk', severity: 'red' },
  medium: { fr: 'Risque moyen', en: 'Medium risk', severity: 'orange' },
  low: { fr: 'Risque faible', en: 'Low risk', severity: 'green' },
};

const REGION_LABEL_FONT_SIZE = 38;
/**
 * Rendered size of a marker icon in the export (SVG/world units).
 * Chosen as ~1.75× the interactive base size (32) to keep markers clearly
 * visible without overwhelming dense clusters — the larger it is, the more
 * push-apart it triggers, which would distort the icon arrangement.
 */
const MARKER_ICON_SIZE = 56;
const LIST_ICON_SIZE = 22;
/**
 * Inverse-scale factor used for the export's de-overlap computation.
 * Mirrors `1 / MIN_ICON_SCALE` from the interactive view (MapPage.tsx) so the
 * export sees the same collision zones the user sees at maximum interactive
 * zoom — keeping the icon arrangement identical between the UI and the PNG.
 */
function findNearestLabel(
  wx: number,
  wy: number,
  warLabels: ReadonlyArray<ExportWarLabel>,
  kind: 'major' | 'minor',
): string | null {
  let best: ExportWarLabel | null = null;
  let bestDist = Infinity;
  for (const lbl of warLabels) {
    if (lbl.kind !== kind) continue;
    const dx = lbl.wx - wx;
    const dy = lbl.wy - wy;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = lbl;
    }
  }
  return best?.text ?? null;
}

const hexPoints = HEX_VERTICES.map((p) => `${p.x},${p.y}`).join(' ');

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WarPlanExportView({
  bbox,
  markers,
  collisionContext,
  arrows,
  disabledRegionKeys,
  mapBackground,
  warPlan,
  warLabels,
  caption,
}: WarPlanExportViewProps): React.JSX.Element {
  // Compute SVG render height that preserves the bbox aspect ratio.
  const aspect = bbox.width / bbox.height;
  let mapWidth = EXPORT_MAP_WIDTH_PX;
  let mapHeight = mapWidth / aspect;
  if (mapHeight > EXPORT_MAP_MAX_HEIGHT_PX) {
    mapHeight = EXPORT_MAP_MAX_HEIGHT_PX;
    mapWidth = mapHeight * aspect;
  }

  // Split markers by category, sorted, for the right-column lists.
  const combatTargets = markers
    .filter((m) => m.kind === 'combat')
    .slice()
    .sort(
      (a, b) =>
        (COMBAT_ORDER[a.metadata.iconId] ?? 99) - (COMBAT_ORDER[b.metadata.iconId] ?? 99),
    );
  const hubs = markers
    .filter((m) => EXPORT_HUB_IDS.has(m.metadata.iconId))
    .slice()
    .sort(
      (a, b) => (HUB_ORDER[a.metadata.iconId] ?? 99) - (HUB_ORDER[b.metadata.iconId] ?? 99),
    );

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    padding: 16,
    background: '#0f172a',
    color: '#f1f5f9',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    width: mapWidth + EXPORT_PANEL_WIDTH_PX + 32 + 16,
  };

  const mapColumnStyle: CSSProperties = {
    position: 'relative',
    width: mapWidth,
    height: mapHeight,
    background: '#020617',
    borderRadius: 6,
    overflow: 'hidden',
  };

  const captionStyle: CSSProperties = {
    position: 'absolute',
    bottom: 6,
    right: 8,
    fontSize: 12,
    color: '#cbd5e1',
    textShadow: '0 0 4px rgba(0,0,0,0.9)',
  };

  const inkCreditStyle: CSSProperties = {
    position: 'absolute',
    bottom: 6,
    left: 8,
    fontSize: 10,
    color: '#94a3b8',
    textShadow: '0 0 4px rgba(0,0,0,0.9)',
  };

  return (
    <div style={containerStyle}>
      <div style={mapColumnStyle}>
        <svg
          width={mapWidth}
          height={mapHeight}
          viewBox={`${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', background: '#020617' }}
        >
          {/* Tile bitmaps — only active hexagons */}
          <g>
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <image
                  key={tile.key}
                  href={tileAssetFor(tile, mapBackground)}
                  x={tile.x - TILE_W / 2}
                  y={tile.y - TILE_H / 2}
                  width={TILE_W}
                  height={TILE_H}
                  preserveAspectRatio="none"
                />
              );
            })}
          </g>

          {/* Hex outlines (subtle) */}
          <g pointerEvents="none">
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <polygon
                  key={`outline-${tile.key}`}
                  points={hexPoints}
                  transform={`translate(${tile.x} ${tile.y})`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeOpacity={0.45}
                  strokeWidth={2}
                />
              );
            })}
          </g>

          {/* Markers: combat + hubs */}
          <g pointerEvents="none">
            {(() => {
              // De-overlap is computed over the full collision context (or
              // markers alone if none provided) so the export arrangement
              // matches what the user sees on the interactive map.
              // Render order matches the interactive view (oldest first).
              const contextSource = collisionContext ?? markers;
              const sortedContext = [...contextSource].sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
              );
              const rects = sortedContext.map((m) => ({
                id: m.id,
                x: m.position.x,
                y: m.position.y,
                scale: m.metadata.scale ?? 1,
                // Use the actual rendered size so AABB matches visual bounds.
                size: MARKER_ICON_SIZE * (m.metadata.scale ?? 1),
              }));
              const adjusted = resolveIconCollisions(rects);
              const renderOrder = [...markers].sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
              );
              return renderOrder.map((m) => {
                const entry = getIconEntry(m.kind, m.metadata.iconId);
                const size = MARKER_ICON_SIZE * (m.metadata.scale ?? 1);
                const pos = adjusted.get(m.id) ?? m.position;
                return (
                  <image
                    key={m.id}
                    href={entry.src}
                    x={pos.x - size / 2}
                    y={pos.y - size / 2}
                    width={size}
                    height={size}
                    preserveAspectRatio="xMidYMid meet"
                  />
                );
              });
            })()}
          </g>

          <ExportArrows arrows={arrows} />

          {/* Region labels — always visible */}
          <g pointerEvents="none">
            {ATLAS.map((tile) => {
              if (disabledRegionKeys.has(tile.key)) return null;
              return (
                <text
                  key={`label-${tile.key}`}
                  x={tile.x}
                  y={tile.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={REGION_LABEL_FONT_SIZE}
                  fontFamily="sans-serif"
                  fontWeight={700}
                  fill="#ffffff"
                  stroke="#0f172a"
                  strokeWidth={4}
                  style={{ paintOrder: 'stroke fill' }}
                >
                  {REGION_LABELS[tile.key] ?? tile.key}
                </text>
              );
            })}
          </g>
        </svg>

        {mapBackground === 'ink' ? <div style={inkCreditStyle}>{INK_CREDIT}</div> : null}
        {caption ? <div style={captionStyle}>{caption}</div> : null}
      </div>

      <ExportPanel
        warPlan={warPlan}
        combatTargets={combatTargets}
        hubs={hubs}
        warLabels={warLabels}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right column: static war-plan panel + marker lists
// ---------------------------------------------------------------------------

interface ExportPanelProps {
  warPlan: WarPlanDTO | null;
  combatTargets: ReadonlyArray<MapObject>;
  hubs: ReadonlyArray<MapObject>;
  warLabels: ReadonlyArray<ExportWarLabel>;
}

function ExportPanel({
  warPlan,
  combatTargets,
  hubs,
  warLabels,
}: ExportPanelProps): React.JSX.Element {
  const wrapper: CSSProperties = {
    width: EXPORT_PANEL_WIDTH_PX,
    background: 'rgba(15, 23, 42, 0.95)',
    border: '1px solid #334155',
    borderRadius: 6,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };

  const titleStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    paddingBottom: 8,
    borderBottom: '1px solid #334155',
    color: '#f1f5f9',
  };

  return (
    <aside style={wrapper}>
      <WarPlanStrategyBlock warPlan={warPlan} titleStyle={titleStyle} />

      {combatTargets.length > 0 && (
        <MarkerListSection
          fr="Cibles"
          en="Combat targets"
          kind="combat"
          items={combatTargets}
          warLabels={warLabels}
        />
      )}
      {hubs.length > 0 && (
        <MarkerListSection
          fr="Logistique"
          en="Logistics hubs"
          kind="logistic"
          items={hubs}
          warLabels={warLabels}
        />
      )}
    </aside>
  );
}

interface ExportSectionProps {
  fr: string;
  en: string;
  label: { fr: string; en: string; severity: Severity };
}

/**
 * Reusable war-plan strategy block: title + 4 severity-coloured pills
 * (war style, op frequency, stock management, griefing risk). Used by both
 * export views to keep the same visual identity.
 */
export function WarPlanStrategyBlock({
  warPlan,
  titleStyle,
}: {
  warPlan: WarPlanDTO | null;
  titleStyle: CSSProperties;
}): React.JSX.Element {
  if (!warPlan) {
    return <p style={{ color: '#94a3b8', fontSize: 14 }}>War plan indisponible.</p>;
  }
  const sections: Array<{
    fr: string;
    en: string;
    label: { fr: string; en: string; severity: Severity };
  }> = [
    { fr: 'Type de guerre', en: 'War style', label: WAR_STYLE_LABELS[warPlan.warStyle] },
    {
      fr: "Fréquence d'opération",
      en: 'Operation frequency',
      label: OP_FREQ_LABELS[warPlan.opFrequency],
    },
    {
      fr: 'Gestion des stocks',
      en: 'Stock management',
      label: STOCK_LABELS[warPlan.stockManagement],
    },
    {
      fr: 'Risque de grief / alting',
      en: 'Griefing / alting risk',
      label: GRIEF_LABELS[warPlan.griefingRisk],
    },
  ];
  return (
    <>
      <div style={titleStyle}>{warPlan.title}</div>
      {sections.map((s) => (
        <ExportSection key={s.fr} fr={s.fr} en={s.en} label={s.label} />
      ))}
    </>
  );
}

function ExportSection({ fr, en, label }: ExportSectionProps): React.JSX.Element {
  const headerStyle: CSSProperties = {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#cbd5e1',
    lineHeight: 1.15,
  };
  const pillStyle: CSSProperties = {
    background: SEVERITY_BG[label.severity],
    color: '#ffffff',
    padding: '8px 10px',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
  const dotStyle: CSSProperties = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: SEVERITY_DOT[label.severity],
    flexShrink: 0,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={headerStyle}>
        <div>{fr}</div>
        <div style={{ color: '#94a3b8', fontSize: 11 }}>{en}</div>
      </div>
      <div style={pillStyle}>
        <span style={dotStyle} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{label.fr}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>{label.en}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marker list section (combat targets / logistics hubs)
// ---------------------------------------------------------------------------

interface MarkerListSectionProps {
  fr: string;
  en: string;
  kind: 'combat' | 'logistic';
  items: ReadonlyArray<MapObject>;
  warLabels: ReadonlyArray<ExportWarLabel>;
}

function MarkerListSection({
  fr,
  en,
  kind,
  items,
  warLabels,
}: MarkerListSectionProps): React.JSX.Element {
  const headerStyle: CSSProperties = {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#cbd5e1',
    lineHeight: 1.15,
    paddingTop: 6,
    borderTop: '1px solid #334155',
  };
  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '4px 6px',
    background: 'rgba(30, 41, 59, 0.6)',
    borderRadius: 4,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={headerStyle}>
        <div>
          {fr} <span style={{ color: '#94a3b8' }}>({items.length})</span>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 11 }}>{en}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {items.map((obj) => {
          const entry = getIconEntry(kind, obj.metadata.iconId);
          const region = findRegionLabel(obj.position.x, obj.position.y);
          const major = findNearestLabel(obj.position.x, obj.position.y, warLabels, 'major');
          const minor = findNearestLabel(obj.position.x, obj.position.y, warLabels, 'minor');
          const note = obj.metadata.note?.trim();
          return (
            <div key={obj.id} style={rowStyle}>
              <img
                src={entry.src}
                alt=""
                width={LIST_ICON_SIZE}
                height={LIST_ICON_SIZE}
                style={{ objectFit: 'contain', flexShrink: 0, marginTop: 1 }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
                  {note || entry.label}
                </div>
                <div style={{ color: '#cbd5e1', fontSize: 11, lineHeight: 1.25 }}>
                  {region}
                  {major ? ` / ${major}` : ''}
                </div>
                {minor && (
                  <div style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.25 }}>
                    {minor}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
